<template>
  <v-container class="mt-4">
    <v-data-table
      class="border-table"
      density="compact"
      :headers="headers"
      hide-default-footer
      hide-no-data
      item-value="id"
      :items="variants">
      <template #item="{ item }">
        <tr>
          <td>{{ item.variant_name }}</td>
          <td>
            <v-icon @click="changeActiveVariant(item.id, item.is_active_part)">
              {{
                item.is_active_part === 1
                  ? "mdi-checkbox-outline"
                  : "mdi-checkbox-blank-outline"
              }}
            </v-icon>
          </td>
          <td class="text-no-wrap">
            <EmbeddedTableActionButtons
              :clone-action="() => cloneAction(item.id)"
              :delete-action="() => deleteAction(item.id)"
              :edit-action="() => editAction(item.id)"
              :delete-disabled="
                variants.length === 1 || item.is_active_part === 1
              "
              :delete-disabled-callback="
                () => deleteDisabledCallback(item.is_active_part)
              " />
          </td>
        </tr>
      </template>

      <template #[`header.actions`]>
        <div class="text-center pb-4">
          <v-btn
            class="mx-1 px-0 mt-4"
            color="#ECE6F0"
            elevated="26"
            max-width="40"
            min-width="40"
            rounded="lg"
            data-cy="add-element-variant"
            @click="addAction">
            <v-icon
              color="#0F6DAC"
              size="x-large">
              mdi-plus
            </v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </v-container>
</template>

<script setup>
import { computed } from "vue";
import { useTranslation } from "i18next-vue";

import EmbeddedTableActionButtons from "@/components/DataTable/EmbeddedTableActionButtons.vue";
import { useSpaceStore } from "@/stores/spaceStore";
import { useRouter } from "vue-router";
import { useSnackbar } from "vuetify-use-dialog";
import { useCalculationStore } from "@/stores/calculationStore";

const { t } = useTranslation();
const spaceStore = useSpaceStore();
const calculationStore = useCalculationStore();

const router = useRouter();
const createSnackbar = useSnackbar();

const props = defineProps({
  partId: {
    type: String,
    required: true
  }
});

const part = computed(() => {
  return spaceStore.getElement(props.partId);
});

const variants = computed(() => {
  return spaceStore
    .getElementList(part.value.parent, "part")
    .filter((element) => element.variant === part.value.variant)
    .map((element) => ({
      id: element.id,
      variant_name: element.variant_name,
      is_active_part: Number(element.is_active_part)
    }));
});

const headers = computed(() => [
  {
    title: t("entities.variant.fields.name"),
    align: "start",
    sortable: false,
    key: "variant_name"
  },
  {
    title: t("entities.variant.fields.active"),
    key: "is_active_part",
    sortable: false
  },
  {
    title: "",
    key: "actions",
    sortable: false
  }
]);

const addAction = () => {
  router.push({
    name: "calculations-variant",
    params: {
      nodeId: spaceStore.createVariant(props.partId)
    }
  });
};

const editAction = (id) => {
  router.push({
    name: "calculations-variant",
    params: { nodeId: id + "_variant" }
  });
};

const cloneAction = (id) => {
  spaceStore.prepareClone(id + "_part");
  spaceStore.cloneOnServer(props.partId, false, true);
};

const deleteAction = (id) => {
  if (part.value.is_active_part) {
    createSnackbar({
      text: t("actions.last-variant"),
      showCloseButton: false,
      snackbarProps: { color: "error", timeout: 2000 }
    });
    return;
  }
  spaceStore.removeElement(id + "_part", props.partId, true);
};

const deleteDisabledCallback = (is_active) => {
  createSnackbar({
    text: t(is_active ? "actions.active-variant" : "actions.last-variant"),
    showCloseButton: false,
    snackbarProps: { color: "error", timeout: 3000 }
  });
};

function changeActiveVariant(id, currentState) {
  if (currentState) {
    // cant disable active variant
  } else {
    const oldActiveVariant = variants.value.filter(
      (variant) => variant.is_active_part
    )[0].id;
    calculationStore.changeActiveVariant(
      oldActiveVariant + "_part",
      id + "_part"
    );
  }
}
</script>
